// Alertas dos usuários (PO1-2194, A6; refeita na PO1-3389) — o admin do
// embarcador escolhe quem recebe cada aviso e por qual canal, e cuida das
// tarefas. Dois modos:
//   - Por alerta: avisos em linhas, agrupados, com os inscritos em chips e
//     "+ Inscrever"; tarefas à parte (não têm inscrição: vão para o responsável),
//     com a chave "Usar tarefas de fatura", quem não pode ser responsável e as
//     tarefas abertas.
//   - Por usuário: avisos do usuário (Sino / E-mail) e a preferência de como as
//     tarefas chegam (sem "Desligado"), mais "Pode ser responsável por fatura".
//
// Sem `?.`/`??`: o build roda em CRA 3.4 / Node 12.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FormGroup, Input, Label, Spinner, Table } from "reactstrap";

import PageHero, { PageHeroRefreshButton } from "../../../components/PageHero";
import ShellCard, { TableSurface } from "../../../components/ShellCard";
import SpecificEmpresaRequiredNotice from "../../../components/SpecificEmpresaRequiredNotice";
import { AUTOPILOT_COLORS } from "../../../components/autopilot/styles";
import { useCompanies } from "../../../hooks/companies";
import { useToast } from "../../../hooks/toast";
import {
  definirResponsavelFatura,
  listarInscricoesEmbarcador,
  removerInscricaoUsuario,
  salvarInscricaoUsuario,
  salvarUsoTarefasFatura,
} from "../../../services/alertasApi";
import {
  FREQUENCIA_CANAL_LABEL,
  ORDEM_GRUPOS,
  PRODUTO_LABEL,
  canalCurto,
  descreverCanais,
  ehAlertaDeTeste,
  ehRelatorio,
  ehTarefa,
  inscricaoRelatorio,
  formatarDataHora,
  frequenciaPadraoDe,
  grupoDoAlerta,
  inscricaoPadrao,
  mensagemErro,
  normalizarInscricao,
  opcoesFrequenciaCanal,
  opcoesFrequenciaTarefa,
} from "../../Alertas/alertasUtils";
import "./AlertasUsuarios.css";

const mutedStyle = { color: AUTOPILOT_COLORS.muted, fontSize: "0.78rem" };
const titleStyle = { color: AUTOPILOT_COLORS.title, fontWeight: 600 };
const TODOS = "__todos__";
const VAZIO = { usuarios: [], alertas: [], inscricoes: [], tarefasFatura: null };

// Ids vêm do SQL Server (bigint) como texto; compara sempre como texto.
const mesmoId = (a, b) => String(a) === String(b);
const chave = (idUsuario, idAlerta) => `${idUsuario}:${idAlerta}`;
const contem = (texto, busca) =>
  String(texto || "")
    .toLowerCase()
    .indexOf(busca) !== -1;
const plural = (n, um, varios) => `${n} ${n === 1 ? um : varios}`;

// Select de um canal do aviso (PO1-3249): Desligado / Na hora / Resumo diário.
// Desligar os dois canais remove a inscrição.
function SelectCanal({ alerta, canal, atual, ocupado, onSalvar }) {
  const opcoes = opcoesFrequenciaCanal(alerta, canal);
  const rotulo = canal === "sino" ? "Sino" : "E-mail";
  return (
    <Input
      type="select"
      bsSize="sm"
      value={atual[canal] || ""}
      disabled={ocupado || opcoes.length <= 1}
      aria-label={`${rotulo} de ${alerta.nome}`}
      onChange={(e) => {
        const v = e.target.value;
        const nova = { sino: atual.sino, email: atual.email };
        nova[canal] = v || null;
        onSalvar(nova.sino || nova.email ? nova : null);
      }}
    >
      {opcoes.map((f) => (
        <option key={f || "off"} value={f}>
          {FREQUENCIA_CANAL_LABEL[f] || f}
        </option>
      ))}
    </Input>
  );
}

// Select de um canal da tarefa: só as frequências (tarefa não se desliga).
function SelectCanalTarefa({ alerta, canal, atual, ocupado, onSalvar }) {
  const opcoes = opcoesFrequenciaTarefa(alerta, canal);
  const rotulo = canal === "sino" ? "Sino" : "E-mail";
  if (!opcoes.length) return <span style={mutedStyle}>Não se aplica</span>;
  return (
    <Input
      type="select"
      bsSize="sm"
      value={atual[canal] || opcoes[0]}
      disabled={ocupado || opcoes.length <= 1}
      aria-label={`${rotulo} de ${alerta.nome}`}
      onChange={(e) => {
        const v = e.target.value;
        const nova = { sino: atual.sino, email: atual.email };
        nova[canal] = v;
        onSalvar(nova);
      }}
    >
      {opcoes.map((f) => (
        <option key={f} value={f}>
          {FREQUENCIA_CANAL_LABEL[f] || f}
        </option>
      ))}
    </Input>
  );
}

// "+ Inscrever": usuários ainda fora do aviso, com Sino / E-mail / os dois na
// frequência padrão do catálogo.
function PainelInscrever({ alerta, usuarios, ocupados, onInscrever, onFechar }) {
  const permitidos = alerta.canaisPermitidos || [];
  const freq = frequenciaPadraoDe(alerta);
  const opcoes = [];
  if (permitidos.indexOf("SINO") !== -1) opcoes.push({ rotulo: "Sino", insc: { sino: freq, email: null } });
  if (permitidos.indexOf("EMAIL") !== -1) opcoes.push({ rotulo: "E-mail", insc: { sino: null, email: freq } });
  if (opcoes.length === 2) opcoes.push({ rotulo: "Sino + e-mail", insc: { sino: freq, email: freq } });
  return (
    <div className="alertas-usr__painel">
      <div style={{ ...mutedStyle, marginBottom: 6 }}>
        Inscrever em <strong style={titleStyle}>{alerta.nome}</strong> ({FREQUENCIA_CANAL_LABEL[freq] || freq})
      </div>
      {usuarios.length === 0 ? <div style={mutedStyle}>Todos os usuários já estão inscritos.</div> : null}
      {usuarios.map((u) => (
        <div key={u.id} className="alertas-usr__painel-linha">
          <span>{u.nome}</span>
          <span className="alertas-usr__chips">
            {opcoes.map((o) => (
              <button
                key={o.rotulo}
                type="button"
                className="alertas-usr__chip-btn"
                disabled={!!ocupados[chave(u.id, alerta.idAlerta)]}
                aria-label={`Inscrever ${u.nome} em ${alerta.nome} por ${o.rotulo}`}
                onClick={() => onInscrever(u, o.insc)}
              >
                {o.rotulo}
              </button>
            ))}
          </span>
        </div>
      ))}
      <div style={{ marginTop: 8, textAlign: "right" }}>
        <button type="button" className="alertas-usr__chip-btn" onClick={onFechar}>
          Concluir
        </button>
      </div>
    </div>
  );
}

function LinhaAviso({ alerta, inscritos, livres, aberto, ocupados, onAbrir, onSalvar }) {
  return (
    <div className="alertas-usr__linha">
      <div className="alertas-usr__linha-info">
        <div style={titleStyle}>{alerta.nome}</div>
        <div style={mutedStyle}>{alerta.descricao}</div>
      </div>
      <div className="alertas-usr__linha-acoes">
        {inscritos.length === 0 ? <span style={mutedStyle}>Ninguém inscrito</span> : null}
        {inscritos.map((x) => (
          <span key={x.usuario.id} className="alertas-usr__pessoa">
            <span>{x.usuario.nome}</span>
            <small>{canalCurto(x.inscricao)}</small>
            <button
              type="button"
              className="alertas-usr__x"
              aria-label={`Remover ${x.usuario.nome} de ${alerta.nome}`}
              disabled={!!ocupados[chave(x.usuario.id, alerta.idAlerta)]}
              onClick={() => onSalvar(x.usuario, null)}
            >
              ×
            </button>
          </span>
        ))}
        <button type="button" className="alertas-usr__chip-btn" onClick={onAbrir} aria-expanded={aberto}>
          + Inscrever
        </button>
      </div>
      {aberto ? (
        <PainelInscrever
          alerta={alerta}
          usuarios={livres}
          ocupados={ocupados}
          onInscrever={(u, insc) => onSalvar(u, insc)}
          onFechar={onAbrir}
        />
      ) : null}
    </div>
  );
}

function CartaoTarefa({ alerta, tarefasFatura, usuarios, nomeEmbarcador, salvandoChave, onAlternarTarefas }) {
  const ehFatura = !!tarefasFatura && alerta.codigo === tarefasFatura.codigo;
  const ligada = !ehFatura || tarefasFatura.usar !== false;
  const podem = usuarios.filter((u) => u.podeSerResponsavel !== false);
  const naoPodem = usuarios.filter((u) => u.podeSerResponsavel === false);
  const abertas = ehFatura ? tarefasFatura.abertas || [] : [];
  return (
    <div className="alertas-usr__linha">
      <div className="alertas-usr__linha-info">
        <div style={titleStyle}>
          {alerta.nome} <span style={{ ...mutedStyle, fontWeight: 400 }}>· automático para o responsável</span>
        </div>
        <div style={mutedStyle}>{alerta.descricao}</div>
        {ehFatura ? (
          <FormGroup check style={{ marginTop: 10 }}>
            <Label check>
              <Input
                type="checkbox"
                checked={ligada}
                disabled={salvandoChave}
                aria-label="Usar tarefas de fatura"
                onChange={(e) => {
                  const marcado = e.target.checked;
                  onAlternarTarefas(marcado);
                }}
              />
              <span className="form-check-sign">
                <span className="check" />
              </span>
              Usar tarefas de fatura{nomeEmbarcador ? ` em ${nomeEmbarcador}` : ""}
            </Label>
          </FormGroup>
        ) : null}
      </div>
      <div className="alertas-usr__linha-acoes" style={{ display: "block" }}>
        {!ligada ? (
          <div className="alertas-usr__nota">
            Desligado{nomeEmbarcador ? ` para ${nomeEmbarcador}` : ""}. A tratativa continua funcionando e o
            responsável fica registrado na fatura, sem tarefa, sino nem e-mail.
          </div>
        ) : (
          <>
            <div className="alertas-usr__rotulo" style={{ marginTop: 0 }}>
              Quem recebe
            </div>
            <span className="alertas-usr__todos">
              Todos os usuários
              <small>
                {ehFatura ? `que podem ser responsáveis (${podem.length})` : "escolhidos como responsáveis"} · padrão:{" "}
                {descreverCanais(inscricaoPadrao(alerta))}
              </small>
            </span>
            <div style={{ ...mutedStyle, marginTop: 6 }}>
              Recebe quem for escolhido como responsável. Cada usuário escolhe como a tarefa chega em Meus alertas.
            </div>
            {ehFatura && naoPodem.length ? (
              <>
                <div className="alertas-usr__rotulo">Não podem ser responsáveis</div>
                <div className="alertas-usr__chips">
                  {naoPodem.map((u) => (
                    <span key={u.id} className="alertas-usr__pessoa alertas-usr__pessoa--sem-x">
                      {u.nome}
                    </span>
                  ))}
                </div>
                <div style={{ ...mutedStyle, marginTop: 6 }}>Para mudar, abra o usuário em Por usuário.</div>
              </>
            ) : null}
          </>
        )}
        {ehFatura ? (
          <>
            <div className="alertas-usr__rotulo">Abertas agora</div>
            {abertas.length === 0 ? <span style={mutedStyle}>Nenhuma tarefa aberta</span> : null}
            {abertas.map((t) => (
              <div key={t.id} style={{ fontSize: "0.85rem", marginBottom: 4 }}>
                <span style={titleStyle}>{t.nomeResponsavel || "Sem responsável"}</span>
                <span style={mutedStyle}> · </span>
                {t.link ? <Link to={t.link}>{t.mensagem || `Item ${t.entidadeId}`}</Link> : t.mensagem}
                <span style={mutedStyle}> desde {formatarDataHora(t.criadoEm)}</span>
              </div>
            ))}
          </>
        ) : null}
      </div>
    </div>
  );
}

export default function AlertasUsuarios() {
  const { addToast } = useToast();
  const { empresa } = useCompanies();
  // Mesmo jeito das outras telas do embarcador (ex.: CanaisRecepcao): o id do
  // embarcador é o `value` da opção do seletor do cabeçalho.
  const embarcadorId = empresa && !empresa.isAllOption ? empresa.value : null;
  const nomeEmbarcador = empresa && empresa.label ? empresa.label : "";

  const [dados, setDados] = useState(VAZIO);
  const [carregando, setCarregando] = useState(false);
  const [ocupados, setOcupados] = useState({});
  const [salvandoChave, setSalvandoChave] = useState(false);
  const [modo, setModo] = useState("alerta");
  const [filtroProduto, setFiltroProduto] = useState(TODOS);
  const [busca, setBusca] = useState("");
  const [aberto, setAberto] = useState(null);
  const [idSelecionado, setIdSelecionado] = useState(null);

  const flash = useCallback(
    (type, description) =>
      addToast({ place: "tr", type, title: type === "success" ? "OK" : "Atenção", description }),
    [addToast],
  );

  const carregar = useCallback(async () => {
    if (!embarcadorId) return;
    setCarregando(true);
    try {
      const data = await listarInscricoesEmbarcador(embarcadorId);
      setDados({
        usuarios: (data && data.usuarios) || [],
        alertas: ((data && data.alertas) || []).filter((a) => !ehAlertaDeTeste(a)),
        inscricoes: (data && data.inscricoes) || [],
        tarefasFatura: (data && data.tarefasFatura) || null,
      });
    } catch (e) {
      flash("danger", mensagemErro(e, "Falha ao carregar as inscrições dos usuários."));
    } finally {
      setCarregando(false);
    }
  }, [embarcadorId, flash]);

  useEffect(() => {
    setDados(VAZIO);
    setAberto(null);
    setIdSelecionado(null);
    carregar();
  }, [carregar]);

  const mapa = useMemo(() => {
    const m = {};
    dados.inscricoes.forEach((i) => {
      m[chave(i.idUsuario, i.idAlerta)] = normalizarInscricao(i);
    });
    return m;
  }, [dados.inscricoes]);

  const produtosNosAlertas = useMemo(() => {
    const set = {};
    dados.alertas.forEach((a) => {
      set[a.produto || ""] = true;
    });
    return Object.keys(set);
  }, [dados.alertas]);

  const termo = busca.trim().toLowerCase();
  const doProduto = (a) => filtroProduto === TODOS || !a.produto || a.produto === filtroProduto;
  // PO1-3384: relatórios (um e-mail por embarcador, por dia útil) ficam num bloco próprio.
  const avisos = dados.alertas.filter((a) => !ehTarefa(a) && !ehRelatorio(a) && doProduto(a));
  const tarefas = dados.alertas.filter((a) => ehTarefa(a) && doProduto(a));
  const relatorios = dados.alertas.filter((a) => ehRelatorio(a) && doProduto(a));
  const inscricaoDe = (usuario, alerta) => mapa[chave(usuario.id, alerta.idAlerta)] || null;

  // ── Escritas ──────────────────────────────────────────────────────────────

  const salvar = async (usuario, alerta, inscricao) => {
    const k = chave(usuario.id, alerta.idAlerta);
    setOcupados((p) => ({ ...p, [k]: true }));
    try {
      let nova = null;
      if (inscricao) {
        const data = await salvarInscricaoUsuario(usuario.id, alerta.idAlerta, {
          embarcadorId,
          sino: inscricao.sino,
          email: inscricao.email,
        });
        nova = normalizarInscricao((data && data.inscricao) || inscricao);
      } else {
        await removerInscricaoUsuario(usuario.id, alerta.idAlerta, embarcadorId);
      }
      setDados((prev) => {
        const resto = prev.inscricoes.filter(
          (i) => !(mesmoId(i.idUsuario, usuario.id) && mesmoId(i.idAlerta, alerta.idAlerta)),
        );
        const inscricoes = nova ? resto.concat([{ idUsuario: usuario.id, idAlerta: alerta.idAlerta, ...nova }]) : resto;
        return { ...prev, inscricoes };
      });
      flash("success", `${alerta.nome}: ${nova ? "inscrição salva" : "inscrição removida"} para ${usuario.nome}.`);
    } catch (e) {
      flash("danger", mensagemErro(e, "Falha ao salvar a inscrição."));
    } finally {
      setOcupados((p) => ({ ...p, [k]: false }));
    }
  };

  const alternarTarefas = async (usar) => {
    setSalvandoChave(true);
    try {
      await salvarUsoTarefasFatura(embarcadorId, usar);
      setDados((prev) => ({ ...prev, tarefasFatura: { ...(prev.tarefasFatura || {}), usar } }));
      flash("success", usar ? "Tarefas de fatura ligadas." : "Tarefas de fatura desligadas. A tratativa continua sem tarefa.");
    } catch (e) {
      flash("danger", mensagemErro(e, "Falha ao salvar a chave das tarefas de fatura."));
    } finally {
      setSalvandoChave(false);
    }
  };

  const alternarResponsavel = async (usuario, pode) => {
    const k = `resp:${usuario.id}`;
    setOcupados((p) => ({ ...p, [k]: true }));
    try {
      const data = await definirResponsavelFatura(usuario.id, embarcadorId, pode);
      const valor = data && typeof data.podeSerResponsavel === "boolean" ? data.podeSerResponsavel : pode;
      setDados((prev) => ({
        ...prev,
        usuarios: prev.usuarios.map((u) => (mesmoId(u.id, usuario.id) ? { ...u, podeSerResponsavel: valor } : u)),
      }));
      flash("success", `${usuario.nome} ${valor ? "pode" : "não pode"} ser responsável por fatura.`);
    } catch (e) {
      flash("danger", mensagemErro(e, "Falha ao salvar a permissão."));
    } finally {
      setOcupados((p) => ({ ...p, [k]: false }));
    }
  };

  // ── Blocos ────────────────────────────────────────────────────────────────

  const hero = (
    <PageHero
      eyebrow="Gestão de acessos"
      title="Alertas dos usuários"
      description="Escolha quem recebe cada aviso e por qual canal. As tarefas não precisam de inscrição: vão sempre para o responsável."
      actions={embarcadorId ? <PageHeroRefreshButton onClick={carregar} loading={carregando} /> : null}
    >
      {embarcadorId ? (
        <div className="alertas-usr__chips" style={{ marginTop: "1rem" }} role="tablist" aria-label="Modo de visualização">
          {[
            ["alerta", "Por alerta"],
            ["usuario", "Por usuário"],
          ].map(([valor, rotulo]) => (
            <button
              key={valor}
              type="button"
              role="tab"
              aria-selected={modo === valor}
              className={`alertas-usr__chip-btn${modo === valor ? " is-active" : ""}`}
              onClick={() => setModo(valor)}
            >
              {rotulo}
            </button>
          ))}
        </div>
      ) : null}
    </PageHero>
  );

  if (!embarcadorId) {
    return (
      <div className="content">
        {hero}
        <div style={{ marginTop: "1.25rem" }}>
          <SpecificEmpresaRequiredNotice message="Selecione um embarcador específico no cabeçalho para gerenciar os alertas dos usuários dele." />
        </div>
      </div>
    );
  }

  const barra = (
    <div className="alertas-usr__barra">
      {produtosNosAlertas.length > 1 ? (
        <div className="alertas-usr__chips" role="group" aria-label="Produto">
          <span style={mutedStyle}>Produto:</span>
          {[TODOS].concat(produtosNosAlertas.filter((p) => p)).map((p) => (
            <button
              key={p}
              type="button"
              className={`alertas-usr__chip-btn${filtroProduto === p ? " is-active" : ""}`}
              onClick={() => {
                setFiltroProduto(p);
                setAberto(null);
              }}
            >
              {p === TODOS ? "Todos" : PRODUTO_LABEL[p] || p}
            </button>
          ))}
        </div>
      ) : null}
      <Input
        type="search"
        bsSize="sm"
        className="alertas-usr__busca"
        placeholder={modo === "alerta" ? "Buscar alerta ou usuário" : "Buscar usuário"}
        aria-label="Buscar"
        value={busca}
        onChange={(e) => {
          const v = e.target.value;
          setBusca(v);
        }}
      />
    </div>
  );

  const carregandoVazio = carregando && dados.usuarios.length === 0;

  const porAlerta = () => {
    // Busca: casa com o nome do aviso ou com o nome de alguém inscrito nele.
    const avisosVisiveis = avisos.filter(
      (a) =>
        !termo ||
        contem(a.nome, termo) ||
        dados.usuarios.some((u) => inscricaoDe(u, a) && contem(u.nome, termo)),
    );
    const grupos = ORDEM_GRUPOS.map((g) => ({ nome: g, alertas: avisosVisiveis.filter((a) => grupoDoAlerta(a) === g) })).filter(
      (g) => g.alertas.length,
    );
    return (
      <>
        <ShellCard
          eyebrow={
            <span className="alertas-usr__selo">
              <i className="tim-icons icon-bell-55" /> Avisos
            </span>
          }
          description="Chegam só para quem está inscrito."
        >
          {grupos.map((g) => (
            <div key={g.nome} className="alertas-usr__grupo">
              <div className="alertas-usr__grupo-titulo">{g.nome}</div>
              {g.alertas.map((a) => {
                const inscritos = dados.usuarios
                  .filter((u) => inscricaoDe(u, a))
                  .map((u) => ({ usuario: u, inscricao: inscricaoDe(u, a) }));
                const livres = dados.usuarios.filter((u) => !inscricaoDe(u, a));
                return (
                  <LinhaAviso
                    key={a.idAlerta}
                    alerta={a}
                    inscritos={inscritos}
                    livres={livres}
                    aberto={aberto === a.idAlerta}
                    ocupados={ocupados}
                    onAbrir={() => setAberto(aberto === a.idAlerta ? null : a.idAlerta)}
                    onSalvar={(u, insc) => salvar(u, a, insc)}
                  />
                );
              })}
            </div>
          ))}
          {!carregando && grupos.length === 0 ? (
            <div style={{ ...mutedStyle, padding: "0.75rem 0" }}>Nenhum aviso disponível.</div>
          ) : null}
          {carregandoVazio ? (
            <div style={{ padding: "1rem", textAlign: "center" }}>
              <Spinner size="sm" />
            </div>
          ) : null}
        </ShellCard>
        {relatorios.length ? (
          <ShellCard
            eyebrow={
              <span className="alertas-usr__selo">
                <i className="tim-icons icon-paper" /> Relatórios
              </span>
            }
            description="Um e-mail por dia útil para o embarcador, com a planilha anexa, para quem estiver inscrito e para os contatos do embarcador do tipo do relatório (tela Contatos)."
          >
            {relatorios
              .filter((a) => !termo || contem(a.nome, termo) || dados.usuarios.some((u) => inscricaoDe(u, a) && contem(u.nome, termo)))
              .map((a) => {
                const inscritos = dados.usuarios
                  .filter((u) => inscricaoDe(u, a))
                  .map((u) => ({ usuario: u, inscricao: inscricaoDe(u, a) }));
                const livres = dados.usuarios.filter((u) => !inscricaoDe(u, a));
                return (
                  <LinhaAviso
                    key={a.idAlerta}
                    alerta={a}
                    inscritos={inscritos}
                    livres={livres}
                    aberto={aberto === a.idAlerta}
                    ocupados={ocupados}
                    onAbrir={() => setAberto(aberto === a.idAlerta ? null : a.idAlerta)}
                    onSalvar={(u, insc) => salvar(u, a, insc ? inscricaoRelatorio(a) : null)}
                  />
                );
              })}
          </ShellCard>
        ) : null}
        {tarefas.length ? (
          <ShellCard
            eyebrow={
              <span className="alertas-usr__selo alertas-usr__selo--tarefa">
                <i className="tim-icons icon-check-2" /> Tarefas
              </span>
            }
            description="Vão sempre para o responsável escolhido na ação, pelo sino e por e-mail. Não precisam de inscrição."
          >
            {tarefas.map((a) => (
              <CartaoTarefa
                key={a.idAlerta}
                alerta={a}
                tarefasFatura={dados.tarefasFatura}
                usuarios={dados.usuarios}
                nomeEmbarcador={nomeEmbarcador}
                salvandoChave={salvandoChave}
                onAlternarTarefas={alternarTarefas}
              />
            ))}
          </ShellCard>
        ) : null}
      </>
    );
  };

  const porUsuario = () => {
    const tf = dados.tarefasFatura;
    const abertasDe = (u) => ((tf && tf.abertas) || []).filter((t) => mesmoId(t.idResponsavel, u.id));
    const usuariosVisiveis = dados.usuarios.filter((u) => !termo || contem(u.nome, termo) || contem(u.email, termo));
    const sel =
      usuariosVisiveis.find((u) => mesmoId(u.id, idSelecionado)) || usuariosVisiveis[0] || null;
    const temTarefaFatura = !!tf && tarefas.some((a) => a.codigo === tf.codigo);
    const resumo = (u) => {
      const qtdAvisos = avisos.filter((a) => inscricaoDe(u, a)).length;
      const qtdTarefas = abertasDe(u).length;
      const partes = [plural(qtdAvisos, "aviso", "avisos")];
      if (qtdTarefas) partes.push(plural(qtdTarefas, "tarefa aberta", "tarefas abertas"));
      if (temTarefaFatura && u.podeSerResponsavel === false) partes.push("não pode ser responsável");
      return partes.join(" · ");
    };
    return (
      <div className="alertas-usr__layout" style={{ marginTop: "1.25rem" }}>
        <ShellCard style={{ marginTop: 0 }} title="Usuários" description={plural(dados.usuarios.length, "usuário", "usuários")}>
          <nav className="alertas-usr__lista-usuarios" aria-label="Usuários">
            {usuariosVisiveis.map((u) => (
              <button
                key={u.id}
                type="button"
                className={`alertas-usr__usuario${sel && mesmoId(sel.id, u.id) ? " is-active" : ""}`}
                aria-current={sel && mesmoId(sel.id, u.id) ? "true" : undefined}
                onClick={() => setIdSelecionado(u.id)}
              >
                <strong>{u.nome}</strong>
                <small>{resumo(u)}</small>
              </button>
            ))}
            {carregandoVazio ? <Spinner size="sm" /> : null}
            {!carregando && usuariosVisiveis.length === 0 ? <span style={mutedStyle}>Nenhum usuário.</span> : null}
          </nav>
        </ShellCard>
        {sel ? (
          <ShellCard style={{ marginTop: 0 }} title={sel.nome} description={sel.email}>
            <div className="alertas-usr__rotulo" style={{ marginTop: 0 }}>
              Avisos
            </div>
            <TableSurface>
              <div className="table-responsive">
                <Table className="text-left hub-thead-accent" style={{ color: AUTOPILOT_COLORS.text }}>
                  <thead className="text-primary">
                    <tr>
                      <th>Aviso</th>
                      <th style={{ width: 170 }}>Sino</th>
                      <th style={{ width: 170 }}>E-mail</th>
                    </tr>
                  </thead>
                  <tbody>
                    {avisos.map((a) => {
                      const atual = normalizarInscricao(inscricaoDe(sel, a));
                      const ocupado = !!ocupados[chave(sel.id, a.idAlerta)];
                      return (
                        <tr key={a.idAlerta}>
                          <td>
                            <div style={titleStyle}>{a.nome}</div>
                            <div style={mutedStyle}>{grupoDoAlerta(a)}</div>
                          </td>
                          <td>
                            <SelectCanal alerta={a} canal="sino" atual={atual} ocupado={ocupado} onSalvar={(i) => salvar(sel, a, i)} />
                          </td>
                          <td>
                            <SelectCanal alerta={a} canal="email" atual={atual} ocupado={ocupado} onSalvar={(i) => salvar(sel, a, i)} />
                          </td>
                        </tr>
                      );
                    })}
                    {avisos.length === 0 ? (
                      <tr>
                        <td colSpan={3} style={{ ...mutedStyle, padding: "1rem" }}>
                          Nenhum aviso disponível.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </Table>
              </div>
            </TableSurface>

            {relatorios.length ? (
              <>
                <div className="alertas-usr__rotulo">Relatórios</div>
                <div style={{ ...mutedStyle, marginBottom: 8 }}>Um e-mail por dia útil, com a planilha anexa. Só e-mail.</div>
                <TableSurface>
                  <div className="table-responsive">
                    <Table className="text-left hub-thead-accent" style={{ color: AUTOPILOT_COLORS.text }}>
                      <thead className="text-primary">
                        <tr>
                          <th>Relatório</th>
                          <th style={{ width: 170 }}>E-mail</th>
                        </tr>
                      </thead>
                      <tbody>
                        {relatorios.map((a) => {
                          const ligado = !!normalizarInscricao(inscricaoDe(sel, a)).email;
                          const ocupado = !!ocupados[chave(sel.id, a.idAlerta)];
                          return (
                            <tr key={a.idAlerta}>
                              <td>
                                <div style={titleStyle}>{a.nome}</div>
                                <div style={mutedStyle}>{a.descricao}</div>
                              </td>
                              <td>
                                <Input
                                  type="select"
                                  bsSize="sm"
                                  value={ligado ? "on" : ""}
                                  disabled={ocupado}
                                  aria-label={`E-mail de ${a.nome} para ${sel.nome}`}
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    salvar(sel, a, v ? inscricaoRelatorio(a) : null);
                                  }}
                                >
                                  <option value="">Desligado</option>
                                  <option value="on">Ligado</option>
                                </Input>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </div>
                </TableSurface>
              </>
            ) : null}

            {tarefas.length ? (
              <>
                <div className="alertas-usr__rotulo">Tarefas</div>
                <div style={{ ...mutedStyle, marginBottom: 8 }}>
                  Chegam sempre que {sel.nome} for o responsável. Aqui só se escolhe como chegam.
                </div>
                {temTarefaFatura ? (
                  <FormGroup check style={{ marginBottom: 8 }}>
                    <Label check>
                      <Input
                        type="checkbox"
                        checked={sel.podeSerResponsavel !== false}
                        disabled={!!ocupados[`resp:${sel.id}`]}
                        aria-label={`${sel.nome} pode ser responsável por fatura`}
                        onChange={(e) => {
                          const marcado = e.target.checked;
                          alternarResponsavel(sel, marcado);
                        }}
                      />
                      <span className="form-check-sign">
                        <span className="check" />
                      </span>
                      Pode ser responsável por fatura (aparece no Encaminhar tratativa)
                    </Label>
                  </FormGroup>
                ) : null}
                {temTarefaFatura && tf.usar === false ? (
                  <div className="alertas-usr__nota" style={{ marginBottom: 8 }}>
                    Tarefas de fatura desligadas{nomeEmbarcador ? ` em ${nomeEmbarcador}` : ""}: a preferência abaixo só vale
                    quando forem ligadas.
                  </div>
                ) : null}
                <TableSurface>
                  <div className="table-responsive">
                    <Table className="text-left hub-thead-accent" style={{ color: AUTOPILOT_COLORS.text }}>
                      <thead className="text-primary">
                        <tr>
                          <th>Tarefa</th>
                          <th style={{ width: 170 }}>Sino</th>
                          <th style={{ width: 170 }}>E-mail</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tarefas.map((a) => {
                          const atual = normalizarInscricao(inscricaoDe(sel, a) || inscricaoPadrao(a));
                          const ocupado = !!ocupados[chave(sel.id, a.idAlerta)];
                          return (
                            <tr key={a.idAlerta}>
                              <td>
                                <div style={titleStyle}>{a.nome}</div>
                                <div style={mutedStyle}>{inscricaoDe(sel, a) ? "Preferência do usuário" : "Padrão"}</div>
                              </td>
                              <td>
                                <SelectCanalTarefa alerta={a} canal="sino" atual={atual} ocupado={ocupado} onSalvar={(i) => salvar(sel, a, i)} />
                              </td>
                              <td>
                                <SelectCanalTarefa alerta={a} canal="email" atual={atual} ocupado={ocupado} onSalvar={(i) => salvar(sel, a, i)} />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </div>
                </TableSurface>
                {temTarefaFatura ? (
                  <>
                    <div className="alertas-usr__rotulo">Tarefas abertas para {sel.nome}</div>
                    {abertasDe(sel).length === 0 ? (
                      <div style={mutedStyle}>Nenhuma tarefa aberta.</div>
                    ) : (
                      abertasDe(sel).map((t) => (
                        <div key={t.id} style={{ fontSize: "0.85rem", marginBottom: 4 }}>
                          {t.link ? <Link to={t.link}>{t.mensagem || `Item ${t.entidadeId}`}</Link> : t.mensagem}
                          <span style={mutedStyle}> desde {formatarDataHora(t.criadoEm)}</span>
                        </div>
                      ))
                    )}
                  </>
                ) : null}
              </>
            ) : null}
          </ShellCard>
        ) : null}
      </div>
    );
  };

  return (
    <div className="content">
      {hero}
      {barra}
      {modo === "alerta" ? porAlerta() : porUsuario()}
    </div>
  );
}
